import { useState, useMemo, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { JobPost, JobFilters as JobFiltersType, JobType } from "../types/job";
import { Briefcase, MapPin, Calendar, DollarSign, Building2, Filter, X } from "lucide-react";
import { getJobPostData } from "@/api/misc";
import { useToast } from "@/components/ui/use-toast";
import { JobSkeleton } from "./JobSkeleton";
import { useTranslation } from "react-i18next";

export function LatestJobs() {
  const { toast } = useToast();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const formatSalaryNumber = (value: number) => Math.round(value).toLocaleString("en-US");

  const jobTypeOptions: { value: JobType; label: string }[] = [
    { value: "full-time", label: t("community.latest_jobs.job_types.full_time") },
    { value: "part-time", label: t("community.latest_jobs.job_types.part_time") },
    { value: "contract", label: t("community.latest_jobs.job_types.contract") },
    { value: "temporary", label: t("community.latest_jobs.job_types.temporary") },
    { value: "apprenticeship", label: t("community.latest_jobs.job_types.apprenticeship") },
  ];
  const [jobs, setJobs] = useState<JobPost[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [filters, setFilters] = useState<JobFiltersType>({
    search: "",
    location: "",
    types: [],
    jobTypes: [],
  });
  const [showFilters, setShowFilters] = useState(false);

  // Fetch jobs from API
  useEffect(() => {
    const fetchJobs = async () => {
      try {
        setIsLoading(true);
        const response = await getJobPostData("sort=match_desc");
        const jobsData = response?.jobs || [];

        // Transform API data to JobPost format
        const transformedJobs: JobPost[] = jobsData.map((job: any) => {
          const address = job.address || {};
          const city = address.city || "";
          const state = address.state || "";
          const location =
            `${city}${city && state ? ", " : ""}${state}`.trim() ||
            t("community.latest_jobs.location_not_specified");

          // Format salary — show whenever the structured salary object has a real
          // bound. The old check (`min && max` truthy) silently dropped any post
          // with a 0 lower bound ("up to $X") or a single bound, so those cards
          // showed no salary even though the job had one. `!= null` matches the
          // "has salary" check the Job Details page uses (0 is a valid bound).
          const salaryTypeLabel =
            job.salary_type === "hourly"
              ? t("community.latest_jobs.per_hour")
              : job.salary_type === "weekly"
                ? t("community.latest_jobs.per_week")
                : "";
          let salary: string | undefined;
          if (job.salary && job.salary.min != null && job.salary.max != null) {
            salary = `$${formatSalaryNumber(job.salary.min)} - $${formatSalaryNumber(job.salary.max)}${salaryTypeLabel}`;
          } else if (job.salary && job.salary.min != null) {
            salary = `$${formatSalaryNumber(job.salary.min)}${salaryTypeLabel}`;
          } else if (job.salary && job.salary.max != null) {
            salary = `$${formatSalaryNumber(job.salary.max)}${salaryTypeLabel}`;
          }

          return {
            id: job.id || job._id,
            title: job.title || t("community.latest_jobs.job_title"),
            company: job.organization?.name || t("community.latest_jobs.company"),
            location,
            type: job.type?.toLowerCase().replace("_", "-") || "full-time",
            salary,
            description: job.details ? job.details.replace(/<[^>]*>/g, "") : "", // Strip HTML tags
            postedDate: new Date(job.created_at || Date.now()),
            requirements: job.skills || [],
            match_percentage: job.match_percentage,
          };
        });

        setJobs(transformedJobs.slice(0, 10)); // Show only first 10 jobs
      } catch (error) {
        console.error("Error fetching jobs:", error);
        toast({
          title: t("community.latest_jobs.error_loading"),
          description: t("community.latest_jobs.failed_to_load"),
          variant: "destructive",
        });
      } finally {
        setIsLoading(false);
      }
    };

    fetchJobs();
  }, [toast]);

  // Filter jobs based on current filters
  const filteredJobs = useMemo(() => {
    return jobs.filter((job) => {
      // Filter by location
      if (
        filters.location &&
        !job.location.toLowerCase().includes(filters.location.toLowerCase())
      ) {
        return false;
      }

      // Filter by job type
      if (filters.jobTypes.length > 0 && !filters.jobTypes.includes(job.type)) {
        return false;
      }

      return true;
    });
  }, [jobs, filters]);

  const formatPostedDate = (date: Date) => {
    const now = new Date();
    const diffInMs = now.getTime() - date.getTime();
    const diffInHours = diffInMs / (1000 * 60 * 60);

    if (diffInHours < 1) {
      const mins = Math.floor(diffInMs / (1000 * 60));
      return `${mins}m ago`;
    } else if (diffInHours < 24) {
      return `${Math.floor(diffInHours)}h ago`;
    } else {
      return `${Math.floor(diffInHours / 24)}d ago`;
    }
  };

  const formatSalary = (
    salary: string | { min: number; max: number; currency: string },
  ): string => {
    if (typeof salary === "string") {
      return salary;
    }
    return `${salary.currency}${formatSalaryNumber(salary.min)} - ${salary.currency}${formatSalaryNumber(salary.max)}`;
  };

  const getJobTypeBadgeVariant = (type: string) => {
    switch (type) {
      case "full-time":
        return "default";
      case "part-time":
        return "secondary";
      case "contract":
        return "outline-solid";
      default:
        return "secondary";
    }
  };

  const getJobTypeLabel = (type: string) => {
    return type
      .split("-")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  };

  const handleLocationChange = (value: string) => {
    setFilters({
      ...filters,
      location: value,
    });
  };

  const handleJobTypeToggle = (jobType: JobType) => {
    const newJobTypes = filters.jobTypes.includes(jobType)
      ? filters.jobTypes.filter((t) => t !== jobType)
      : [...filters.jobTypes, jobType];

    setFilters({
      ...filters,
      jobTypes: newJobTypes,
    });
  };

  const handleClearFilters = () => {
    setFilters({
      search: "",
      location: "",
      types: [],
      jobTypes: [],
    });
  };

  const hasActiveFilters = filters.location || filters.jobTypes.length > 0;

  return (
    <Card className="border-border">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2 text-lg font-bold">
              <Briefcase className="text-primary h-5 w-5" />
              {t("community.latest_jobs.title")}
            </CardTitle>
            <p className="text-muted-foreground mt-1 text-xs">
              {filteredJobs.length}{" "}
              {filteredJobs.length === 1
                ? t("community.latest_jobs.job")
                : t("community.latest_jobs.jobs")}{" "}
              {t("community.latest_jobs.available")}
            </p>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setShowFilters(!showFilters)}
            className={`h-8 w-8 p-0 ${showFilters || hasActiveFilters ? "text-primary" : ""}`}
          >
            <Filter className="h-4 w-4" />
          </Button>
        </div>
      </CardHeader>

      {/* Collapsible Filters Section */}
      {showFilters && (
        <div className="space-y-4 border-b px-6 pb-4">
          <div className="flex items-center justify-between">
            <h4 className="text-sm font-semibold">{t("community.latest_jobs.filters")}</h4>
            {hasActiveFilters && (
              <Button
                variant="ghost"
                size="sm"
                onClick={handleClearFilters}
                className="h-7 gap-1 text-xs"
              >
                <X className="h-3 w-3" />
                {t("community.latest_jobs.clear")}
              </Button>
            )}
          </div>

          {/* Location Filter */}
          <div className="flex flex-col gap-2">
            <Label htmlFor="location" className="flex items-center gap-2 text-xs font-semibold">
              <MapPin className="text-muted-foreground h-3 w-3" />
              {t("community.latest_jobs.location")}
            </Label>
            <Input
              id="location"
              type="text"
              placeholder={t("community.latest_jobs.location_placeholder")}
              value={filters.location}
              onChange={(e) => handleLocationChange(e.target.value)}
              className="h-8 text-xs"
            />
          </div>

          {/* Job Type Filter */}
          <div className="space-y-2">
            <Label className="text-xs font-semibold">{t("community.latest_jobs.job_type")}</Label>
            <div className="space-y-2">
              {jobTypeOptions.map((option) => (
                <div key={option.value} className="flex items-center space-x-2">
                  <Checkbox
                    id={option.value}
                    checked={filters.jobTypes.includes(option.value)}
                    onCheckedChange={() => handleJobTypeToggle(option.value)}
                  />
                  <label
                    htmlFor={option.value}
                    className="cursor-pointer text-xs leading-none font-medium peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                  >
                    {option.label}
                  </label>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      <CardContent className="space-y-4 pt-4">
        {isLoading ? (
          <>
            {Array.from({ length: 3 }).map((_, index) => (
              <JobSkeleton key={`job-skeleton-${index}`} />
            ))}
          </>
        ) : filteredJobs.length === 0 ? (
          <div className="text-muted-foreground py-8 text-center">
            <div className="bg-muted text-muted-foreground mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full">
              <Briefcase className="h-6 w-6" />
            </div>
            <p className="text-sm">{t("community.latest_jobs.no_jobs_match")}</p>
            <p className="mt-1 text-xs">{t("community.latest_jobs.adjust_criteria")}</p>
          </div>
        ) : (
          filteredJobs.map((job) => (
            <div
              key={job.id}
              onClick={() => navigate(`/jobseeker/jobs/detail?id=${job.id}`)}
              className="group border-border bg-card hover:border-brand-accent-border cursor-pointer rounded-xl border p-3 transition-all hover:-translate-y-0.5 hover:shadow-md"
            >
              <div className="space-y-3">
                {/* Header: title + company + match pill */}
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0 flex-1 space-y-1.5">
                    <h3 className="text-foreground group-hover:text-primary line-clamp-2 text-sm leading-tight font-semibold transition-colors">
                      {job.title}
                    </h3>
                    <div className="flex items-center gap-1.5">
                      <Building2 className="text-muted-foreground h-3 w-3 shrink-0" />
                      <p className="text-muted-foreground truncate text-xs font-medium">
                        {job.company}
                      </p>
                    </div>
                  </div>
                  <span className="border-brand-accent-border text-brand-accent-foreground inline-flex shrink-0 items-center gap-1 rounded-full border bg-gradient-to-br from-[#eff6ff] to-[#dbeafe] px-2.5 py-1 text-xs font-bold">
                    {`${Math.round(job?.match_percentage ?? 0)}%`}
                    <span className="font-semibold opacity-80">
                      {t("community.latest_jobs.matched")}
                    </span>
                  </span>
                </div>

                {/* Meta: job type + location */}
                <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5">
                  <Badge
                    variant="secondary"
                    className="border-brand-accent-border bg-brand-accent text-brand-accent-foreground hover:bg-brand-accent shrink-0 text-[11px] font-semibold"
                  >
                    {getJobTypeLabel(job.type)}
                  </Badge>
                  <span className="text-muted-foreground inline-flex min-w-0 items-center gap-1 text-xs">
                    <MapPin className="h-3 w-3 shrink-0" />
                    <span className="truncate">{job.location}</span>
                  </span>
                </div>

                {/* Salary (money green) — its own row so it never collides with the date */}
                {job.salary && (
                  <div className="flex">
                    <span className="inline-flex max-w-full min-w-0 items-center gap-1 rounded-full border border-[#a7f3d0] bg-[#ecfdf5] px-2 py-0.5 text-xs font-semibold text-[#047857]">
                      <DollarSign className="h-3 w-3 shrink-0" />
                      <span className="truncate">{formatSalary(job.salary)}</span>
                    </span>
                  </div>
                )}

                {/* Description */}
                {job.description && (
                  <p className="text-muted-foreground line-clamp-2 text-xs">{job.description}</p>
                )}

                {/* Posted date — fixed position, so cards align with or without salary */}
                <div className="text-muted-foreground flex items-center gap-1 text-[11px]">
                  <Calendar className="h-3 w-3 shrink-0" />
                  {t("community.latest_jobs.posted")} {formatPostedDate(job.postedDate)}
                </div>

                {/* Action */}
                <Button
                  variant="outline-soft"
                  size="sm"
                  className="h-8 w-full text-xs"
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate(`/jobseeker/jobs/detail?id=${job.id}`);
                  }}
                >
                  {t("community.latest_jobs.view_details")}
                </Button>
              </div>
            </div>
          ))
        )}
      </CardContent>
    </Card>
  );
}
